<script setup lang="ts">
type LoaderTone = 'accent' | 'current' | 'muted';
type LoaderSize = 12 | 14 | 15 | 16 | 18 | 20 | 24 | 40;

const props = withDefaults(defineProps<{
    tone?: LoaderTone;
    size?: LoaderSize | null;
    decorative?: boolean;
    label?: string;
    minOpacity?: number;
}>(), {
    tone: 'accent',
    size: null,
    decorative: false,
    minOpacity: 0.15,
    label: '[[[Ładowanie]]]'
});

const opacity = computed(() => Math.min(1, Math.max(0, props.minOpacity)));

const cells = [
    { x: 0, y: 0, begin: '0.09s' },
    { x: 5.5, y: 0, begin: '0.18s' },
    { x: 11, y: 0, begin: '0.27s' },
    { x: 0, y: 5.5, begin: '0s' },
    { x: 5.5, y: 5.5, begin: '0.09s' },
    { x: 11, y: 5.5, begin: '0.18s' },
    { x: 0, y: 11, begin: '0.09s' },
    { x: 5.5, y: 11, begin: '0.18s' },
    { x: 11, y: 11, begin: '0.27s' }
];

const toneClasses: Record<LoaderTone, string> = {
    accent: 'text-primary',
    current: 'text-current',
    muted: 'text-[#cacaca]'
};
const sizeClasses: Record<LoaderSize, string> = {
    12: '!size-[12px]',
    14: '!size-[14px]',
    15: '!size-[15px]',
    16: '!size-[16px]',
    18: '!size-[18px]',
    20: '!size-[20px]',
    24: '!size-[24px]',
    40: '!size-[40px]'
};

const reducedMotion = ref(false);
let motionQuery: MediaQueryList | null = null;

function updateMotionPreference(event: MediaQueryListEvent | MediaQueryList)
{
    reducedMotion.value = event.matches;
}

onMounted(() =>
{
    motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    updateMotionPreference(motionQuery);
    motionQuery.addEventListener('change', updateMotionPreference);
});

onBeforeUnmount(() =>
{
    motionQuery?.removeEventListener('change', updateMotionPreference);
});
</script>

<template>
    <svg
        :class="[
            'inline-block shrink-0 overflow-visible align-middle',
            props.size === null ? 'size-(--edito-loader-size) [--edito-loader-size:40px]' : sizeClasses[props.size],
            toneClasses[props.tone]
        ]"
        viewBox="0 0 15 15"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        :role="props.decorative ? undefined : 'status'"
        :aria-hidden="props.decorative ? 'true' : undefined"
        :aria-label="props.decorative ? undefined : $t(props.label)"
    >
        <rect
            v-for="(cell, index) in cells"
            :key="index"
            :x="cell.x"
            :y="cell.y"
            width="4"
            height="4"
            rx="1"
            :opacity="opacity"
            aria-hidden="true"
        >
            <animate
                v-if="!reducedMotion"
                attributeName="opacity"
                :values="opacity + ';1;' + opacity"
                keyTimes="0;0.45;1"
                calcMode="spline"
                keySplines="0.42 0 0.58 1;0.42 0 0.58 1"
                dur="0.65s"
                :begin="cell.begin"
                repeatCount="indefinite"
            />
        </rect>
    </svg>
</template>
